import type { EmployeeRow } from '@/data/types'
import { divide } from './core'

/** A cohort smaller than this produces NO rank — never a number computed off two people. */
export const MIN_COHORT = 5

export type Direction = 'lowerIsBetter' | 'higherIsBetter'

/** Metrics where lower is better. Everything else is higher-is-better. */
export const LOWER_IS_BETTER = new Set(['costPerClaim', 'touchesPerClaim', 'openClaims'])

function median(xs: number[]): number | null {
  if (xs.length === 0) return null
  const s = [...xs].sort((a, b) => a - b)
  const m = s.length >> 1
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2
}

/**
 * Fold the 607 employee-supervisor rows to one row per person.
 * Eleven people report to two supervisors and appear twice; in people views they
 * must fold to one row, while supervisor rollups still see them under both.
 */
export function foldToPeople(rows: readonly EmployeeRow[]): EmployeeRow[] {
  const byName = new Map<string, EmployeeRow>()
  for (const r of rows) {
    const prev = byName.get(r.employee)
    if (!prev) byName.set(r.employee, { ...r })
    // A duplicated person carries identical measures on both rows; keep the first
    // and drop the supervisor, which is the only field that differs.
    else prev.supervisor = null
  }
  return [...byName.values()]
}

/**
 * Cohorts are built on the UNFILTERED population so that slicing to one department
 * never silently redefines who a person is measured against. Pass the full people
 * list here, not the filtered view.
 */
export function titleCohort(
  allPeople: readonly EmployeeRow[],
  jobTitle: string,
): EmployeeRow[] {
  return allPeople.filter((r) => r.jobTitle === jobTitle && r.claims > 0)
}

export const cohortSize = (allPeople: readonly EmployeeRow[], jobTitle: string) =>
  titleCohort(allPeople, jobTitle).length

/** Per-person cost per claim. Null for anyone with no claims. */
export const personCostPerClaim = (r: EmployeeRow) => divide(r.claimLabor, r.claims)

export function titleMedianCostPerClaim(
  allPeople: readonly EmployeeRow[],
  jobTitle: string,
): number | null {
  const vals = titleCohort(allPeople, jobTitle)
    .map(personCostPerClaim)
    .filter((v): v is number => v !== null)
  return median(vals)
}

/**
 * What this person's distance from their peer median costs in dollars.
 * Positive = above their own peer group, i.e. spending more than peers do.
 * Only produced when the cohort has 5+ working peers. (The app's ranks are built in
 * data/selectors.ts `standing`, on the pool MEASURE-RULES §10.7 describes.)
 */
export function costsAboveMedian(
  allPeople: readonly EmployeeRow[],
  person: EmployeeRow,
): number | null {
  if (cohortSize(allPeople, person.jobTitle) < MIN_COHORT) return null
  const med = titleMedianCostPerClaim(allPeople, person.jobTitle)
  const cpc = personCostPerClaim(person)
  if (med === null || cpc === null) return null
  return (cpc - med) * person.claims
}

export interface RankResult {
  rank: number
  of: number
}

/**
 * Dense rank within the job title, best first. Returns null — "no cohort" — when
 * fewer than 5 working peers exist. The UI must print "no cohort", not a number.
 */
export function rankInTitle(
  allPeople: readonly EmployeeRow[],
  person: EmployeeRow,
  value: (r: EmployeeRow) => number | null,
  direction: Direction,
): RankResult | null {
  const cohort = titleCohort(allPeople, person.jobTitle)
  if (cohort.length < MIN_COHORT) return null

  const mine = value(person)
  if (mine === null) return null

  const vals = cohort
    .map(value)
    .filter((v): v is number => v !== null)

  // DENSE rank: count distinct values that beat mine, then add 1.
  const better = new Set(
    vals.filter((v) => (direction === 'lowerIsBetter' ? v < mine : v > mine)),
  )
  return { rank: better.size + 1, of: cohort.length }
}
